/**
 * Cart Drawer
 * Slide-out drawer from the right showing cart items.
 * Follows the same pattern as checkout-modal.ts.
 */

import { CART_DRAWER_STYLES } from './cart-styles';
import { BASE_STYLES } from '../base';
import type { LocalCartItem } from '../../types';
import { formatPrice } from '../../types';
import { escapeHtml } from '../../utils';
import type { CartManager } from '../../utils/cart-manager';

export interface CartDrawerOptions {
  shadowRoot: ShadowRoot;
  cartManager: CartManager;
  currency: string;
  merchantId: string | number;
  onCheckout?: () => void;
  onClose?: () => void;
}

export function openCartDrawer(options: CartDrawerOptions): () => void {
  const { shadowRoot, cartManager, onCheckout, onClose } = options;
  let currency = options.currency;
  let closed = false;

  // Create container
  const container = document.createElement('div');
  container.className = 'ks-cart-drawer-container';

  // Inject styles
  const style = document.createElement('style');
  style.textContent = BASE_STYLES + CART_DRAWER_STYLES;
  container.appendChild(style);

  // Backdrop
  const backdrop = document.createElement('div');
  backdrop.className = 'ks-drawer-backdrop';
  container.appendChild(backdrop);

  // Drawer
  const drawer = document.createElement('div');
  drawer.className = 'ks-drawer';
  container.appendChild(drawer);

  shadowRoot.appendChild(container);

  // Trigger opening animation
  requestAnimationFrame(() => {
    backdrop.classList.add('ks-visible');
    drawer.classList.add('ks-open');
  });

  // Close function
  function close() {
    if (closed) return;
    closed = true;

    drawer.classList.remove('ks-open');
    backdrop.classList.remove('ks-visible');

    setTimeout(() => {
      unsubscribe();
      container.remove();
      onClose?.();
    }, 350);
  }

  function renderDrawer() {
    const items = cartManager.getItems();
    const itemCount = cartManager.getItemCount();
    const subtotalMinor = cartManager.getSubtotalMinor();
    currency = cartManager.getCurrency();

    // Global formatPrice expects major units, so divide once at the boundary.
    const displaySubtotal = formatPrice(subtotalMinor / 100, currency);

    drawer.innerHTML = `
      <div class="ks-drawer-header">
        <h3 class="ks-drawer-title">
          Cart
          <span class="ks-drawer-count">(${itemCount} ${itemCount === 1 ? 'item' : 'items'})</span>
        </h3>
        <button class="ks-drawer-close" data-action="close">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="ks-drawer-body">
        ${items.length === 0
          ? renderEmpty()
          : `<div class="ks-drawer-items">${items.map(renderItem).join('')}</div>`
        }
      </div>
      ${items.length > 0
        ? `<div class="ks-drawer-footer">
            <div class="ks-drawer-summary">
              <span class="ks-drawer-subtotal-label">Subtotal</span>
              <span class="ks-drawer-subtotal-value">${displaySubtotal}</span>
            </div>
            <button class="ks-drawer-checkout-btn" data-action="checkout">
              Proceed to Checkout
            </button>
          </div>`
        : ''
      }
    `;
  }

  function renderEmpty(): string {
    return `
      <div class="ks-drawer-empty">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
        <div class="ks-drawer-empty-text">Your cart is empty</div>
        <div class="ks-drawer-empty-sub">Browse products to add items</div>
        <button class="ks-drawer-continue-btn" data-action="close">Continue Shopping</button>
      </div>
    `;
  }

  function renderItem(item: LocalCartItem): string {
    // Use formatted_price if available, otherwise calculate from raw price
    // Note: raw price from backend is in cents, so divide by 100 for display
    const unitPrice = item.price || 0;
    const lineTotal = unitPrice * item.quantity;

    // Prefer pre-formatted price, fallback to formatting the calculated total
    // If price appears to be in cents (> 100 and no decimal), convert to dollars
    const displayPrice = item.formatted_price
      ? (item.quantity === 1 ? item.formatted_price : formatPrice(lineTotal / 100, currency))
      : formatPrice(lineTotal / 100, currency);

    return `
      <div class="ks-drawer-item" data-product-id="${item.product_id}" data-variant-id="${item.variant_id || ''}">
        ${item.thumbnail
          ? `<img class="ks-drawer-item-img" src="${escapeHtml(item.thumbnail)}" alt="${escapeHtml(item.title)}" />`
          : `<div class="ks-drawer-item-placeholder">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>`
        }
        <div class="ks-drawer-item-info">
          <div class="ks-drawer-item-title">${escapeHtml(item.title)}</div>
          ${item.variant_name
            ? `<div class="ks-drawer-item-variant">${escapeHtml(item.variant_name)}</div>`
            : ''
          }
          <div class="ks-drawer-item-controls">
            <div class="ks-drawer-item-qty">
              <button class="ks-drawer-qty-btn" data-action="qty-dec" data-product-id="${item.product_id}" data-variant-id="${item.variant_id || ''}">-</button>
              <span class="ks-drawer-qty-value">${item.quantity}</span>
              <button class="ks-drawer-qty-btn" data-action="qty-inc" data-product-id="${item.product_id}" data-variant-id="${item.variant_id || ''}">+</button>
            </div>
            <span class="ks-drawer-item-price">${displayPrice}</span>
          </div>
        </div>
        <button class="ks-drawer-item-remove" data-action="remove" data-product-id="${item.product_id}" data-variant-id="${item.variant_id || ''}">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `;
  }

  // Event handler
  function handleClick(e: Event) {
    const target = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!target) return;

    const action = target.dataset.action;
    const productId = target.dataset.productId ? Number(target.dataset.productId) : undefined;
    // Variant IDs can be numbers or UUID strings - preserve the original type
    const rawVariantId = target.dataset.variantId;
    const variantId: number | string | undefined = rawVariantId
      ? (isNaN(Number(rawVariantId)) ? rawVariantId : Number(rawVariantId))
      : undefined;

    switch (action) {
      case 'close':
        close();
        break;
      case 'checkout':
        onCheckout?.();
        close();
        break;
      case 'qty-dec':
        if (productId != null) {
          const item = cartManager.getItems().find(
            (i) => i.product_id === productId && (variantId == null || i.variant_id === variantId)
          );
          if (item && item.quantity > 1) {
            cartManager.updateQuantity(productId, item.quantity - 1, variantId);
          } else if (item) {
            cartManager.removeItem(productId, variantId);
          }
        }
        break;
      case 'qty-inc':
        if (productId != null) {
          const item = cartManager.getItems().find(
            (i) => i.product_id === productId && (variantId == null || i.variant_id === variantId)
          );
          if (item && item.quantity < 99) {
            cartManager.updateQuantity(productId, item.quantity + 1, variantId);
          }
        }
        break;
      case 'remove':
        if (productId != null) {
          cartManager.removeItem(productId, variantId);
        }
        break;
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      close();
    }
  }

  container.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKeydown);
  backdrop.addEventListener('click', close);

  // Subscribe to cart changes for live updates
  const unsubscribe = cartManager.subscribe(() => {
    if (!closed) {
      renderDrawer();
    }
  });

  // Initial render
  renderDrawer();

  // Cleanup function
  return () => {
    document.removeEventListener('keydown', handleKeydown);
    close();
  };
}
